import React, { useRef, useState, useEffect } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { toast } from 'sonner';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { TopToolbar } from '@/components/editor/TopToolbar';
import { DevicePalette } from '@/components/editor/DevicePalette';
import { DetailDeviceList } from '@/components/editor/DetailDeviceList';
import { Inspector } from '@/components/editor/Inspector';
import { PagesSidebar } from '@/components/editor/PagesSidebar';
import { DiagramCanvas } from '@/components/editor/DiagramCanvas';
import { DetailCanvas } from '@/components/editor/DetailCanvas';
import { LocationCanvas } from '@/components/editor/LocationCanvas';
import { AutoView } from '@/components/editor/AutoView';
import { VlanDialog } from '@/components/editor/VlanDialog';
import { TitleBlockDialog } from '@/components/editor/TitleBlockDialog';
import { ShareDialog } from '@/components/editor/ShareDialog';
import { SearchDialog } from '@/components/editor/SearchDialog';
import { HandbookDialog } from '@/components/editor/HandbookDialog';
import { ConnectionDialog } from '@/components/editor/ConnectionDialog';
import { ProjectSwitcherDialog } from '@/components/editor/ProjectSwitcherDialog';
import { DesignCheckPanel } from '@/components/editor/DesignCheckPanel';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import { exportDrawio, parseDrawioWithReport } from '@/lib/drawio';
import { buildAllPageSVGs } from '@/lib/svgExport';
import { downloadText, exportBrowserPDF, exportServerPDF, exportPrintPDF } from '@/lib/pdf';

export default function Editor() {
  const { t } = useI18n();
  const activePageId = useStore((s) => s.activePageId);
  const pages = useStore((s) => s.doc.pages);
  const loadDoc = useStore((s) => s.loadDoc);
  const newProject = useStore((s) => s.newProject);
  const [vlanOpen, setVlanOpen] = useState(false);
  const [tbOpen, setTbOpen] = useState(false);
  const [newAskOpen, setNewAskOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [pdfNoticeOpen, setPdfNoticeOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [handbookOpen, setHandbookOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const importInput = useRef(null);

  // Quick device search (Cmd/Ctrl+K).
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Register the active project in the local project registry AFTER persist
  // hydration so a stable projectId is assigned once (no duplicate entries).
  useEffect(() => {
    const run = () => useStore.getState().ensureProject();
    if (useStore.persist?.hasHydrated?.()) {
      run();
      return undefined;
    }
    return useStore.persist?.onFinishHydration?.(run);
  }, []);

  const isAuto = activePageId === 'auto-cablelist' || activePageId === 'auto-vlanlist';
  const activePage = pages.find((p) => p.id === activePageId);
  const isDetail = activePage?.type === 'detail';
  const isLocation = activePage?.type === 'location';

  const safeName = () => {
    const n = useStore.getState().doc.name || 'netzwerk';
    return n.replace(/[^a-z0-9_\-]+/gi, '_');
  };

  const doExportDrawio = () => {
    const { doc, language } = useStore.getState();
    try {
      const xml = exportDrawio(doc, language);
      downloadText(xml, `${safeName()}.drawio`, 'application/xml');
      toast.success(t('toast_export_ok'));
    } catch (e) {
      toast.error(t('toast_import_err'));
    }
  };

  const onImportClick = () => importInput.current?.click();
  const doImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { doc: parsed, report } = parseDrawioWithReport(String(reader.result));
        loadDoc(parsed);
        const dropped = (report?.droppedShapes || 0) + (report?.droppedEdges || 0);
        if (dropped > 0) {
          toast.warning(t('toast_import_ok'), {
            description: t('toast_import_dropped', { shapes: report.droppedShapes, edges: report.droppedEdges }),
          });
        } else {
          toast.success(t('toast_import_ok'));
        }
      } catch (err) {
        toast.error(t('toast_import_err'));
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const doPdfBrowser = async () => {
    const { doc, language, showPortVlanBadges, showVlanLegend, showPortLabels } = useStore.getState();
    const svgs = buildAllPageSVGs(doc, language, { showBadges: showPortVlanBadges, showLegend: showVlanLegend, showPortLabels }).map((p) => p.svg);
    try {
      await exportBrowserPDF(svgs, `${safeName()}.pdf`);
      toast.success(t('toast_pdf_ok'));
    } catch (e) {
      const ok = exportPrintPDF(svgs);
      if (ok) toast.success(t('toast_pdf_ok'));
      else toast.error(t('toast_pdf_err'));
    }
  };

  const runPdfServer = async () => {
    const { doc, language, showPortVlanBadges, showVlanLegend, showPortLabels } = useStore.getState();
    const pages = buildAllPageSVGs(doc, language, { showBadges: showPortVlanBadges, showLegend: showVlanLegend, showPortLabels }).map((p) => ({ name: p.name, svg: p.svg }));
    try {
      await exportServerPDF(pages, `${safeName()}.pdf`);
      toast.success(t('toast_pdf_ok'));
    } catch (e) {
      toast.error(t('toast_pdf_err'));
    }
  };

  const doPdfServer = () => setPdfNoticeOpen(true);

  const doShare = () => setShareOpen(true);

  const doNew = () => {
    setNewAskOpen(true);
  };
  const createNewProject = (adoptTitleBlock) => {
    const currentTb = useStore.getState().doc?.titleBlock;
    newProject(adoptTitleBlock ? currentTb : null);
    setNewAskOpen(false);
    toast.success(t('new_project'));
    if (adoptTitleBlock) setTbOpen(true);
  };

  return (
    <ReactFlowProvider>
      <div className="flex h-screen w-full flex-col overflow-hidden bg-background">
        <TopToolbar
          onImportClick={onImportClick}
          onExportDrawio={doExportDrawio}
          onPdfBrowser={doPdfBrowser}
          onPdfServer={doPdfServer}
          onShare={doShare}
          onNew={doNew}
          onOpenProjects={() => setProjectsOpen(true)}
          onOpenVlan={() => setVlanOpen(true)}
          onOpenTitleBlock={() => setTbOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
          onOpenHandbook={() => setHandbookOpen(true)}
          canvasActive={!isAuto}
        />
        <input
          ref={importInput}
          type="file"
          accept=".drawio,.xml,text/xml,application/xml"
          className="hidden"
          onChange={doImport}
          data-testid="import-file-input"
        />

        <div className="min-h-0 flex-1">
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel defaultSize={16} minSize={12} maxSize={26}>
              {isDetail || isLocation ? <DetailDeviceList /> : <DevicePalette />}
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={60}>
              <div className="flex h-full flex-col">
                <div className="min-h-0 flex-1">
                  {isAuto ? (
                    <AutoView kind={activePageId === 'auto-cablelist' ? 'cablelist' : 'vlanlist'} />
                  ) : isDetail ? (
                    <DetailCanvas />
                  ) : isLocation ? (
                    <LocationCanvas />
                  ) : (
                    <DiagramCanvas />
                  )}
                </div>
                <PagesSidebar />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={24} minSize={16} maxSize={34}>
              <Inspector />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </div>

      <VlanDialog open={vlanOpen} onOpenChange={setVlanOpen} />
      <TitleBlockDialog open={tbOpen} onOpenChange={setTbOpen} />
      <AlertDialog open={newAskOpen} onOpenChange={setNewAskOpen}>
        <AlertDialogContent data-testid="new-project-titleblock-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('new_project_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('adopt_titleblock_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setNewAskOpen(false)} data-testid="new-project-cancel">
              {t('cancel')}
            </Button>
            <Button variant="outline" onClick={() => createNewProject(false)} data-testid="new-project-without-tb">
              {t('adopt_titleblock_no')}
            </Button>
            <Button onClick={() => createNewProject(true)} data-testid="new-project-with-tb">
              {t('adopt_titleblock_yes')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <HandbookDialog open={handbookOpen} onOpenChange={setHandbookOpen} />
      <ConnectionDialog />
      <ProjectSwitcherDialog open={projectsOpen} onOpenChange={setProjectsOpen} />
      <DesignCheckPanel onFocusVlan={() => setVlanOpen(true)} />

      <AlertDialog open={pdfNoticeOpen} onOpenChange={setPdfNoticeOpen}>
        <AlertDialogContent data-testid="pdf-server-notice">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pdf_server_notice_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('pdf_server_notice_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pdf-server-cancel">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setPdfNoticeOpen(false);
                runPdfServer();
              }}
              data-testid="pdf-server-confirm"
            >
              {t('pdf_server_notice_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ReactFlowProvider>
  );
}
